import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { getToken } from '../services/auth';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (minutes) => {
  if (minutes == null || Number.isNaN(minutes)) return '-';
  if (minutes < 1) return '<1 min';
  if (minutes < 90) return `${Math.round(minutes)} min`;
  const hrs = minutes / 60;
  return `${hrs.toFixed(1)} h`;
};

const formatName = (user) => {
  if (!user) return '-';
  const { name, surname, email } = user;
  if (name || surname) return [name, surname].filter(Boolean).join(' ');
  return email || '-';
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const TABS = [
  { key: 'legacy', label: 'Legacy BBB Recordings' },
  { key: 'metadata', label: 'Metadata (Program-aware)' },
  { key: 'sessions', label: 'Completed Sessions' },
];

const AdminRecordings = () => {
  const [activeTab, setActiveTab] = useState('legacy');

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legacyPagination, setLegacyPagination] = useState({ page: 0, pageSize: PAGE_SIZE_OPTIONS[0] });

  const [metaRecords, setMetaRecords] = useState([]);
  const [metaInfo, setMetaInfo] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState(null);
  const [metaPagination, setMetaPagination] = useState({ page: 0, pageSize: PAGE_SIZE_OPTIONS[0] });
  const [sessionRecords, setSessionRecords] = useState([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const [sessionPagination, setSessionPagination] = useState({ page: 0, pageSize: PAGE_SIZE_OPTIONS[0] });

  const loadLegacyRecordings = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/admin/recordings');
      const items = Array.isArray(data?.items) ? data.items : [];
      setRecords(items);
      setLegacyPagination((prev) => ({ ...prev, page: 0 }));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load recordings.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMetadataRecordings = async ({ force = false } = {}) => {
    setMetaLoading(true);
    setMetaError(null);
    try {
      const config = force ? { params: { refresh: 'true' } } : undefined;
      const { data } = await api.get('/admin/recordings/metadata', config);
      const items = Array.isArray(data?.items) ? data.items : [];
      setMetaRecords(items);
      setMetaInfo(data?.meta || null);
      setMetaPagination((prev) => ({ ...prev, page: 0 }));
    } catch (err) {
      setMetaError(err?.response?.data?.error || err?.message || 'Failed to load recording metadata.');
      setMetaRecords([]);
      setMetaInfo(null);
    } finally {
      setMetaLoading(false);
    }
  };

  const loadSessionRecords = async () => {
    setSessionLoading(true);
    setSessionError(null);
    try {
      const { data } = await api.get('/admin/recordings/sessions', {
        params: { pageSize: 250 },
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setSessionRecords(items);
      setSessionTotal(Number.isFinite(data?.total) ? data.total : items.length);
      setSessionPagination((prev) => ({ ...prev, page: 0 }));
    } catch (err) {
      setSessionError(err?.response?.data?.error || err?.message || 'Failed to load completed sessions.');
      setSessionRecords([]);
      setSessionTotal(0);
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    loadLegacyRecordings();
    loadMetadataRecordings({ force: true });
    loadSessionRecords();
  }, []);

  const refreshAll = () => {
    loadLegacyRecordings();
    loadMetadataRecordings({ force: true });
    loadSessionRecords();
  };

  const openPlayback = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const downloadZip = useCallback(async (recordID) => {
    if (!recordID) return;
    try {
      const base = api.defaults?.baseURL?.replace(/\/$/, '') || '';
      const token = getToken?.();
      console.info('[AdminRecordings] download attempt', {
        recordID,
        base,
        hasToken: !!token,
      });
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${base}/admin/recordings/${recordID}/download`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      console.info('[AdminRecordings] download response', {
        recordID,
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get('content-type'),
        disposition: response.headers.get('content-disposition'),
      });

      if (!response.ok) {
        let message = `Failed to download recording (HTTP ${response.status})`;
        try {
          const text = await response.text();
          const parsed = JSON.parse(text);
          message = parsed?.error || parsed?.message || message;
        } catch {
          // ignore parse issues
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      link.download = match ? match[1] : `${recordID}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const message = err?.message || 'Failed to download recording.';
      console.error('[AdminRecordings] download error', { recordID, error: err });
      if (activeTab === 'legacy') {
        setError(message);
      } else if (activeTab === 'metadata') {
        setMetaError(message);
      } else {
        setSessionError(message);
      }
    }
  }, [activeTab]);

  const totalLegacy = records.length;
  const totalMeta = metaRecords.length;
  const totalSessions = sessionTotal;

  const legacyColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Recording',
        flex: 1.5,
        minWidth: 240,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {row.name || row.recordID || 'Recording'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.meetingID || '-'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'startTime',
        headerName: 'Start',
        minWidth: 160,
        valueGetter: ({ value }) => formatDateTime(value),
      },
      {
        field: 'endTime',
        headerName: 'End',
        minWidth: 160,
        valueGetter: ({ value }) => formatDateTime(value),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {row.playbackUrl ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewRoundedIcon fontSize="small" />}
                onClick={() => openPlayback(row.playbackUrl)}
              >
                Open
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No link
              </Typography>
            )}
            {row.recordID && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArchiveRoundedIcon fontSize="small" />}
                onClick={() => downloadZip(row.recordID)}
              >
                ZIP
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [downloadZip]
  );

  const metadataColumns = useMemo(
    () => [
      {
        field: 'subject',
        headerName: 'Recording',
        flex: 1.6,
        minWidth: 260,
        sortable: false,
        renderCell: ({ row }) => {
          const session = row.session || {};
          const meetingLine = [row.recordID, row.meetingID].filter(Boolean).join(' | ');
          return (
            <Stack spacing={0.25}>
              <Typography variant="body1" fontWeight={600}>
                {session.subject || row.recordID || 'Recording'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {meetingLine || '-'}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'program',
        headerName: 'Program',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => {
          const session = row.session || {};
          const programLabel = session.program ? session.program.toUpperCase() : '-';
          const gradeLabel = session.gradeLevel != null ? `Grade ${session.gradeLevel}` : null;
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">{programLabel}</Typography>
              {gradeLabel && (
                <Typography variant="body2" color="text.secondary">
                  {gradeLabel}
                </Typography>
              )}
              {session.mathSubject && (
                <Typography variant="body2" color="text.secondary">
                  {session.mathSubject}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'tutor',
        headerName: 'Tutor',
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => {
          const tutor = row.session?.tutor;
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">{formatName(tutor)}</Typography>
              {tutor?.email && (
                <Typography variant="body2" color="text.secondary">
                  {tutor.email}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'students',
        headerName: 'Students',
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }) => {
          const students = Array.isArray(row.session?.students) ? row.session.students : [];
          const preview = students.slice(0, 2);
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">
                {students.length} student{students.length === 1 ? '' : 's'}
              </Typography>
              {preview.map((student) => (
                <Typography key={student._id || student.email} variant="body2" color="text.secondary">
                  {formatName(student)}
                </Typography>
              ))}
              {students.length > 2 && (
                <Typography variant="caption" color="text.secondary">
                  +{students.length - 2} more
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'start',
        headerName: 'Start',
        minWidth: 170,
        valueGetter: (params) => {
          const row = params?.row || {};
          const session = row.session || {};
          const start = session.actualStartAt || session.startTime || row.readyAt;
          return formatDateTime(start);
        },
      },
      {
        field: 'duration',
        headerName: 'Duration',
        minWidth: 120,
        valueGetter: (params) => {
          const row = params?.row || {};
          const duration = row.durationMinutes ?? row.session?.actualDurationMinutes;
          return formatDuration(duration);
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => {
          const label = `${row.readinessState || 'pending'}${row.published ? ' + published' : ''}`;
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">{label}</Typography>
              {row.lastCheckedAt && (
                <Typography variant="body2" color="text.secondary">
                  Checked {formatDateTime(row.lastCheckedAt)}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'metaActions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {row.playbackUrl ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewRoundedIcon fontSize="small" />}
                onClick={() => openPlayback(row.playbackUrl)}
              >
                Open
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No link
              </Typography>
            )}
            {row.recordID && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArchiveRoundedIcon fontSize="small" />}
                onClick={() => downloadZip(row.recordID)}
              >
                ZIP
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [downloadZip]
  );

  const sessionColumns = useMemo(
    () => [
      {
        field: 'subject',
        headerName: 'Session',
        flex: 1.6,
        minWidth: 260,
        sortable: false,
        renderCell: ({ row }) => {
          const secondary = [row.bbbMeetingId, row.recording?.recordID].filter(Boolean).join(' | ');
          return (
            <Stack spacing={0.25}>
              <Typography variant="body1" fontWeight={600}>
                {row.subject || 'Session'}
              </Typography>
              {row.scheduleLabel && (
                <Typography variant="body2" color="text.secondary">
                  {row.scheduleLabel}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {secondary || '-'}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'program',
        headerName: 'Program',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => {
          const programLabel = row.program ? row.program.toUpperCase() : '-';
          const gradeLabel = row.gradeLevel != null ? `Grade ${row.gradeLevel}` : null;
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">{programLabel}</Typography>
              {gradeLabel && (
                <Typography variant="body2" color="text.secondary">
                  {gradeLabel}
                </Typography>
              )}
              {row.mathSubject && (
                <Typography variant="body2" color="text.secondary">
                  {row.mathSubject}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'tutor',
        headerName: 'Tutor',
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{formatName(row.tutor)}</Typography>
            {row.tutor?.email && (
              <Typography variant="body2" color="text.secondary">
                {row.tutor.email}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'students',
        headerName: 'Students',
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }) => {
          const students = Array.isArray(row.students) ? row.students : [];
          const preview = students.slice(0, 2);
          return (
            <Stack spacing={0.25}>
              <Typography variant="body2">
                {row.studentCount ?? students.length} student{(row.studentCount ?? students.length) === 1 ? '' : 's'}
              </Typography>
              {preview.map((student) => (
                <Typography key={student._id || student.email} variant="body2" color="text.secondary">
                  {formatName(student)}
                </Typography>
              ))}
              {(row.studentCount ?? students.length) > 2 && (
                <Typography variant="caption" color="text.secondary">
                  +{(row.studentCount ?? students.length) - 2} more
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'scheduled',
        headerName: 'Scheduled',
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{formatDateTime(row.startTime)}</Typography>
            {row.endTime && (
              <Typography variant="body2" color="text.secondary">
                to {formatDateTime(row.endTime)}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'actualTimes',
        headerName: 'Actual',
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{formatDateTime(row.actualStartAt)}</Typography>
            {row.actualEndAt && (
              <Typography variant="body2" color="text.secondary">
                to {formatDateTime(row.actualEndAt)}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'sessionActions',
        headerName: 'Actions',
        width: 240,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewRoundedIcon fontSize="small" />}
              onClick={() => openPlayback(row.playbackUrl || row.recording?.playbackUrl)}
              disabled={!row.playbackUrl && !row.recording?.playbackUrl}
            >
              Open
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArchiveRoundedIcon fontSize="small" />}
              onClick={() => downloadZip(row.recording?.recordID || row.recordID)}
              disabled={!row.recording?.recordID && !row.recordID}
            >
              ZIP
            </Button>
          </Stack>
        ),
      },
    ],
    [downloadZip]
  );
  const LegacyEmptyOverlay = () => (
    <Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {error ? 'Unable to load recordings' : 'No recordings found'}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {error ? error : 'Recordings will appear here once sessions are recorded.'}
      </Typography>
      <Button variant="contained" onClick={refreshAll} startIcon={<RefreshRoundedIcon />}>
        Retry
      </Button>
    </Stack>
  );

  const MetadataEmptyOverlay = () => (
    <Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {metaError ? 'Unable to load metadata' : 'No metadata records yet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {metaError ? metaError : 'You will see enriched recording details once sessions are processed.'}
      </Typography>
      <Button variant="contained" onClick={refreshAll} startIcon={<RefreshRoundedIcon />}>
        Retry
      </Button>
    </Stack>
  );

  const SessionsEmptyOverlay = () => (
    <Stack height="100%" alignItems="center" justifyContent="center" spacing={1.5} sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {sessionError ? 'Unable to load sessions' : 'No completed sessions yet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {sessionError ? sessionError : 'Completed sessions will appear here once classes conclude.'}
      </Typography>
      <Button variant="contained" onClick={refreshAll} startIcon={<RefreshRoundedIcon />}>
        Retry
      </Button>
    </Stack>
  );

  const activeLoading =
    activeTab === 'legacy' ? loading : activeTab === 'metadata' ? metaLoading : sessionLoading;

  return (
    <DashboardLayout title="Recordings">
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="h4" fontWeight={800} color="primary.main">
            Recordings
          </Typography>
          <Tooltip title="Reload all recordings and metadata">
            <span>
              <Button
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={refreshAll}
                disabled={loading || metaLoading || sessionLoading}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {activeLoading && <LinearProgress />}

        {activeTab === 'legacy' && error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {activeTab === 'metadata' && metaError && (
          <Alert severity="error" onClose={() => setMetaError(null)}>
            {metaError}
          </Alert>
        )}
        {activeTab === 'sessions' && sessionError && (
          <Alert severity="error" onClose={() => setSessionError(null)}>
            {sessionError}
          </Alert>
        )}

        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`Legacy: ${totalLegacy}`} color="primary" variant="outlined" />
            <Chip label={`Metadata: ${totalMeta}`} color="secondary" variant="outlined" />
            <Chip label={`Sessions: ${totalSessions}`} color="success" variant="outlined" />
            {metaInfo?.refreshedAt && (
              <Chip
                label={`Synced ${formatDateTime(metaInfo.refreshedAt)}`}
                color="success"
                variant="outlined"
              />
            )}
          </Stack>
        </Paper>

        {activeTab === 'legacy' ? (
          <Paper sx={{ p: 0 }}>
            <Box sx={{ height: 620, width: '100%' }}>
              <DataGrid
                rows={records}
                columns={legacyColumns}
                getRowId={(row) => row.recordID || `${row.meetingID}-${row.startTime}`}
                disableRowSelectionOnClick
                paginationModel={legacyPagination}
                onPaginationModelChange={setLegacyPagination}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                loading={loading}
                density="comfortable"
                hideFooterSelectedRowCount
                sx={{ border: 0, '& .MuiDataGrid-cell': { alignItems: 'center' } }}
                slots={{
                  noRowsOverlay: LegacyEmptyOverlay,
                  noResultsOverlay: LegacyEmptyOverlay,
                }}
              />
            </Box>
          </Paper>
        ) : activeTab === 'metadata' ? (
          <Paper sx={{ p: 0 }}>
            <Box sx={{ height: 620, width: '100%' }}>
              <DataGrid
                rows={metaRecords}
                columns={metadataColumns}
                getRowId={(row) => row.recordID || `${row.meetingID}-${row.readyAt}`}
                disableRowSelectionOnClick
                paginationModel={metaPagination}
                onPaginationModelChange={setMetaPagination}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                loading={metaLoading}
                density="comfortable"
                hideFooterSelectedRowCount
                sx={{
                  border: 0,
                  '& .MuiDataGrid-cell': { alignItems: 'flex-start' },
                  '& .MuiDataGrid-cellContent': { width: '100%' },
                }}
                slots={{
                  noRowsOverlay: MetadataEmptyOverlay,
                  noResultsOverlay: MetadataEmptyOverlay,
                }}
              />
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ p: 0 }}>
            <Box sx={{ height: 620, width: '100%' }}>
              <DataGrid
                rows={sessionRecords}
                columns={sessionColumns}
                getRowId={(row) => row.sessionId || row._id || row.bbbMeetingId || `${row.subject}-${row.startTime}`}
                disableRowSelectionOnClick
                paginationModel={sessionPagination}
                onPaginationModelChange={setSessionPagination}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                loading={sessionLoading}
                density="comfortable"
                hideFooterSelectedRowCount
                sx={{
                  border: 0,
                  '& .MuiDataGrid-cell': { alignItems: 'flex-start' },
                  '& .MuiDataGrid-cellContent': { width: '100%' },
                }}
                slots={{
                  noRowsOverlay: SessionsEmptyOverlay,
                  noResultsOverlay: SessionsEmptyOverlay,
                }}
              />
            </Box>
          </Paper>
        )}
      </Stack>
    </DashboardLayout>
  );
};

export default AdminRecordings;
